import { ButtonGroup, Flex, FormControl, FormLabel, Heading, Input, Select, Text } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import ButtonBack from 'components/ui/ButtonBack';
import ButtonBackToList from 'components/ui/ButtonBackToList';
import CardForm from 'components/ui/CardForm';
import CustomRangeDatepicker from 'components/ui/Datepicker/CustomRangeDatepicker';
import FixedBar from 'components/ui/FixedBar';
import { ResourceSelect, SelectOption } from 'components/ui/ResourceCreatableSelect';
import SaveButton from 'components/ui/SaveButton';
import SubmitButton from 'components/ui/SubmitButton';
import WrapperDialog from 'components/ui/WrapperDialog';
import { format } from 'date-fns';
import { wrapperDialogDefaultProps } from 'features/emails/config';
import { useMessages } from 'hooks/useMessages';
import { ArrayRequiredMinLenTestFunction } from 'lib/validation';
import { FormProvider, Resolver, SubmitHandler, useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useSaveAgenda } from '../hooks/useSaveAgenda';

export interface FormValues {
  contentGroupId: string;
  list: SelectOption;
  dates: Date[];
  title: string;
  onlySave?: boolean;
}

const validationSchema = Yup.object().shape({
  contentGroupId: Yup.string().required(),
  list: Yup.object().required(),
  dates: Yup.mixed().test('dates', '', ArrayRequiredMinLenTestFunction(2)),
  title: Yup.string().required()
});

export function FormData({ isEditing, agenda, contentGroup }: any) {
  const navigate = useNavigate();
  const location = useLocation();

  const defaultValues = {
    contentGroupId: agenda?.contentGroupId || contentGroup?.id,
    list: agenda?.list ? {
      value: agenda.list.id,
      label: `${agenda.list.company.name} - ${agenda.list.name}`
    } : null,
    dates: agenda ? [new Date(agenda.startsAt), new Date(agenda.endsAt)] : [],
    title: agenda?.title
  };

  const methods = useForm<FormValues>({
    mode: 'all',
    resolver: yupResolver(validationSchema) as unknown as Resolver<FormValues>,
    defaultValues
  });

  const { register, setValue, control, handleSubmit, watch, formState: { errors, isValid } } = methods;
  const dates = watch('dates');
  
  const { onSuccess } = useMessages({ success: 'Fique tranquilo, seu trabalho foi salvo' });

  const agendaId = agenda?.id;

  const { onSaveAgenda, isPending, variables } = useSaveAgenda({
    agendaId: isEditing ? agendaId : null,
    onSuccess: (agenda, variables) => {
      if (variables.onlySave) {
        onSuccess();
      }
      else  
        navigate(`/disparos-de-agenda/editar/${agenda.id}/notas${location.search}`);
    }
  });

  const onSubmit: SubmitHandler<FormValues> = ({ list, ...data }) => onSaveAgenda({
    listId: list?.value as string,
    ...data
  });

  const onSave: SubmitHandler<FormValues> = (data) => onSubmit({...data, onlySave: true});

  if (agenda)
    contentGroup = agenda.contentGroup;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex 
          justifyContent="space-between"
        >
          <Heading as="h1" size="h1">
            Identificação
            <Text as="span">{format(new Date(), "EEEE, dd 'de' LLLL 'de' yyyy")}</Text>
          </Heading>

          <ButtonBackToList
            to={{
              pathname: '/disparos-de-agenda',
              search: location.search
            }}
          />
        </Flex>
        
        <CardForm>
          <FormControl isRequired>
            <FormLabel>Modelo de disparo</FormLabel>
            <Select disabled>
              <option value={contentGroup?.id}>{contentGroup?.name}</option>
            </Select>
          </FormControl>
          <FormControl isInvalid={!!errors.list} isRequired>
            <FormLabel>Lista</FormLabel>
            <ResourceSelect
              control={control}
              name="list"
              resourceKey="lists"
              listPath="/lists"
              listParams={{ contentGroupId: defaultValues.contentGroupId }}
              nameFormat={(record) => `${record.company.name} - ${record.name}`}
              placeholder="Selecione uma opção"
            />
          </FormControl>
          <FormControl isInvalid={!!errors.title} isRequired>
            <FormLabel>Assunto do e-mail</FormLabel>
            <Input {...register('title')} maxLength={100} />
          </FormControl>
          
          <FormControl isInvalid={!!errors.dates} isRequired>
            <FormLabel>Filtrar notas por período</FormLabel>
            <CustomRangeDatepicker
              dates={dates ?? []}
              onDateChange={(dates: Date[]) => setValue('dates', dates, { shouldValidate: true })}
            />
          </FormControl>
        </CardForm>
        
        <FixedBar>
          <WrapperDialog {...wrapperDialogDefaultProps}>
            <ButtonBack 
              as={Link}
              to={{
                pathname: '/disparos-de-agenda',
                search: location.search
              }}
            />
          </WrapperDialog>

          <ButtonGroup>
            <SaveButton 
              isDisabled={!isValid}
              isLoading={variables?.onlySave && isPending}
              onClick={handleSubmit(onSave)}
            >
              Salvar
            </SaveButton>

            <SubmitButton 
              isDisabled={!isValid}
              isLoading={!variables?.onlySave && isPending}
            >
              Salvar e continuar
            </SubmitButton>
          </ButtonGroup>
        </FixedBar>
      </form>
    </FormProvider>
  )
}
